import { formatDate } from "@/lib/format";
import { useState } from "react";
import { Link, useParams } from "react-router";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  accountsApi,
  accountTransactionsApi,
  accountNotesApi,
  accountStatementsApi,
  accountTermsApi,
  type AccountDto,
  type AccountTransactionDto,
  type AccountNoteDto,
  type AccountEventDto,
  type CreateAccountNoteRequest,
  type UpdateAccountNoteRequest,
  type IssueStatementRequest,
  type EmailStatementRequest,
} from "@/lib/clients-accounts";
import { useAuth } from "@/app/auth-context";
import { usePermissions } from "@/hooks/use-permissions";
import { DetailPage } from "@/archetypes/DetailPage";
import { TransactionRow } from "@/components/clients-accounts";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { TooltipProvider } from "@/components/ui/tooltip";
import { DetailModal } from "@/components/detail-modal";
import { RecordPaymentModal } from "@/pages/payments/RecordPaymentModal";
import { ChangeStatusDrawer } from "@/pages/accounts/components/ChangeStatusDrawer";
import { EditAccountDialog } from "@/pages/accounts/components/EditAccountDialog";
import { AdjustAccountDialog } from "@/pages/accounts/components/AdjustAccountDialog";
import { RequestTermsApprovalDialog } from "@/pages/accounts/components/RequestTermsApprovalDialog";
import { ApproveTermsDialog } from "@/pages/accounts/components/ApproveTermsDialog";
import { EmailThreadTab } from "@/components/email/EmailThreadTab";
import { cn } from "@/lib/utils";
import { toast } from "@/hooks/use-toast";

// ---------------------------------------------------------------------------
// Currency formatter
// ---------------------------------------------------------------------------

const fmt = new Intl.NumberFormat("en-AU", { style: "currency", currency: "AUD" });

// ---------------------------------------------------------------------------
// StatementsTab
// ---------------------------------------------------------------------------

function StatementsTab({ accountId }: { accountId: number }) {
  const { data, isLoading } = useQuery({
    queryKey: ["account-statements", accountId, "preview"],
    queryFn: () => accountStatementsApi.list(accountId, "pageSize=6"),
  });
  if (isLoading) return <p className="text-sm text-muted-foreground">Loading…</p>;
  const items = data?.items ?? [];
  if (items.length === 0)
    return (
      <div className="space-y-2">
        <p className="text-sm text-muted-foreground">No statements issued yet.</p>
        <Link to={`/accounts/${accountId}/statements`} className="underline text-sm">Open statements page</Link>
      </div>
    );
  return (
    <div className="space-y-2">
      <ul className="text-sm">
        {items.map((s) => (
          <li key={s.statementId} className="flex justify-between border-b py-1">
            <Link to={`/accounts/${accountId}/statements/${s.statementId}`} className="underline">{s.statementNo}</Link>
            <span>{formatDate(s.statementDate)}</span>
            <span>{fmt.format(s.closingBalance)}</span>
          </li>
        ))}
      </ul>
      <Link to={`/accounts/${accountId}/statements`} className="underline text-sm">View all</Link>
    </div>
  );
}

// ---------------------------------------------------------------------------
// TermsTab
// ---------------------------------------------------------------------------

function TermsTab({ accountId }: { accountId: number }) {
  const { data: account } = useQuery({ queryKey: ["account", accountId], queryFn: () => accountsApi.get(accountId) });
  const { data: terms } = useQuery({
    queryKey: ["account-terms-current", account?.termsAcceptedId],
    queryFn: () => accountTermsApi.get(account!.termsAcceptedId!),
    enabled: !!account?.termsAcceptedId,
  });
  return (
    <div className="space-y-3 text-sm">
      {account?.termsAccepted && terms ? (
        <>
          <p>Accepted v{terms.versionNumber} on {new Date(terms.acceptedOn ?? "").toLocaleString("en-AU")} by {terms.acceptedByName}</p>
          <p>Method: {terms.acceptedViaIssuedLinkId ? "External (magic-link)" : "Internal (Finance)"}</p>
        </>
      ) : (
        <p className="text-muted-foreground">No terms accepted yet.</p>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// OverviewTab
// ---------------------------------------------------------------------------

function OverviewTab({ account }: { account: AccountDto }) {
  return (
    <div className="space-y-6">
      {/* Hero balance */}
      <div className="text-center">
        <p className="text-xs text-muted-foreground uppercase tracking-wide">Current balance outstanding</p>
        <p className="text-4xl font-bold">{fmt.format(account.currentBalanceOutstanding)}</p>
      </div>

      {/* Aging buckets */}
      <section>
        <h3 className="mb-2 font-medium text-sm">Aging buckets</h3>
        <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
          {[
            { label: "Current",    value: account.currentStmtPeriodBalanceOwing },
            { label: "30 days",    value: account.previousStmtPeriodBalanceOwing },
            { label: "60 days",    value: account.pastStmtPeriodBalanceOwing },
            { label: "90+ days",   value: account.past2StmtPeriodBalanceOwing },
          ].map(({ label, value }) => (
            <div key={label} className="rounded-md border p-3 text-center">
              <p className="text-xs text-muted-foreground">{label}</p>
              <p className="font-semibold text-sm">{fmt.format(value)}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Payment details */}
      <section>
        <h3 className="mb-2 font-medium text-sm">Payment details</h3>
        <dl className="grid grid-cols-1 gap-2 text-sm sm:grid-cols-2">
          <OverviewRow label="Payment reference"   value={account.pymtReferenceNo || "—"} />
          <OverviewRow label="Opening balance"     value={fmt.format(account.openingBalance)} />
          <OverviewRow label="MYOB balance"        value={fmt.format(account.myobBalanceOutstanding)} />
          <OverviewRow label="Credit period"       value={account.creditPeriodDays != null ? `${account.creditPeriodDays} days` : "—"} />
          <OverviewRow label="Stmt delivery"       value={account.stmtDeliveryMethod ?? "—"} />
          <OverviewRow label="Last payment"        value={account.lastPaymentDate ? `${fmt.format(account.lastPaymentAmount ?? 0)} on ${formatDate(account.lastPaymentDate)}` : "—"} />
          <OverviewRow label="Next payment due"    value={account.nextPaymentDueDate ? `${fmt.format(account.nextPaymentAmountDue ?? 0)} on ${formatDate(account.nextPaymentDueDate)}` : "—"} />
          <OverviewRow label="Last statement"      value={account.lastStatementIssuedDate ? formatDate(account.lastStatementIssuedDate) : "—"} />
          <OverviewRow label="Terms accepted"      value={account.termsAccepted ? `Yes${account.termsAcceptedOn ? ` (${formatDate(account.termsAcceptedOn)})` : ""}` : "No"} />
          <OverviewRow label="Created"             value={account.createdOn ? new Date(account.createdOn).toLocaleString("en-AU") : "—"} />
          <OverviewRow label="Created by"          value={account.createdByName ?? "—"} />
        </dl>
      </section>

      {/* Totals & adjustments */}
      <section>
        <h3 className="mb-2 font-medium text-sm">Totals &amp; adjustments</h3>
        <dl className="grid grid-cols-1 gap-2 text-sm sm:grid-cols-2">
          <OverviewRow label="Total adjustments"        value={account.totalAdjustments != null ? fmt.format(account.totalAdjustments) : "—"} />
          <OverviewRow label="Total refunds"            value={account.totalRefunds != null ? fmt.format(account.totalRefunds) : "—"} />
          <OverviewRow label="Total withdrawals"        value={account.totalWithdrawals != null ? fmt.format(account.totalWithdrawals) : "—"} />
          <OverviewRow label="Payments this FY"         value={account.totalPaymentsThisFinYear != null ? fmt.format(account.totalPaymentsThisFinYear) : "—"} />
          <OverviewRow label="Payments last FY"         value={account.totalPaymentsLastFinYear != null ? fmt.format(account.totalPaymentsLastFinYear) : "—"} />
          <OverviewRow label="Overdue count"            value={account.overdueCount != null ? String(account.overdueCount) : "—"} />
        </dl>
      </section>
    </div>
  );
}

function OverviewRow({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <dt className="text-xs text-muted-foreground">{label}</dt>
      <dd className="font-medium">{value}</dd>
    </div>
  );
}

// ---------------------------------------------------------------------------
// TransactionsTab
// ---------------------------------------------------------------------------

function TransactionsTab({ accountId, openingBalance }: { accountId: number; openingBalance: number }) {
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [typeFilter, setTypeFilter] = useState("");

  const qs = [
    dateFrom ? `dateFrom=${dateFrom}` : "",
    dateTo ? `dateTo=${dateTo}` : "",
    typeFilter ? `tranTypeCode=${typeFilter}` : "",
  ]
    .filter(Boolean)
    .join("&");

  const { data, isLoading } = useQuery({
    queryKey: ["account-tx", accountId, qs],
    queryFn: () => accountTransactionsApi.list(accountId, qs),
  });

  // Compute running balances client-side from openingBalance
  const rows = data?.items ?? [];
  let running = openingBalance;
  const withRunning: Array<{ tran: AccountTransactionDto; runningBalance: number }> = [];
  for (const tran of rows) {
    running += tran.isCredit ? tran.tranAmount : -tran.tranAmount;
    withRunning.push({ tran, runningBalance: running });
  }

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap gap-2">
        <div>
          <Label htmlFor="tx-from">From</Label>
          <Input id="tx-from" type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} className="w-36" />
        </div>
        <div>
          <Label htmlFor="tx-to">To</Label>
          <Input id="tx-to" type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} className="w-36" />
        </div>
        <div>
          <Label htmlFor="tx-type">Type</Label>
          <Input id="tx-type" value={typeFilter} onChange={(e) => setTypeFilter(e.target.value)} placeholder="e.g. INV" className="w-24" />
        </div>
      </div>
      {isLoading ? (
        <p className="text-sm text-muted-foreground">Loading…</p>
      ) : withRunning.length === 0 ? (
        <p className="text-sm text-muted-foreground">No transactions.</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b text-left text-xs text-muted-foreground">
                <th className="px-2 py-1">Date</th>
                <th className="px-2 py-1">Type</th>
                <th className="px-2 py-1">Invoice</th>
                <th className="px-2 py-1 text-right">Amount</th>
                <th className="px-2 py-1 text-right">Running</th>
                <th className="px-2 py-1">Comment</th>
              </tr>
            </thead>
            <tbody>
              {withRunning.map(({ tran, runningBalance }) => (
                <TransactionRow key={tran.tranId} tran={tran} runningBalance={runningBalance} />
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// NotesTab
// ---------------------------------------------------------------------------

function NotesTab({ accountId }: { accountId: number }) {
  const qc = useQueryClient();
  const [newText, setNewText] = useState("");
  const [editingId, setEditingId] = useState<number | null>(null);
  const [editText, setEditText] = useState("");

  const { data, isLoading } = useQuery({
    queryKey: ["account-notes", accountId],
    queryFn: () => accountNotesApi.list(accountId),
  });

  const addMut = useMutation({
    mutationFn: (body: CreateAccountNoteRequest) => accountNotesApi.post(accountId, body),
    onSuccess: () => {
      toast.success("Note added");
      setNewText("");
      void qc.invalidateQueries({ queryKey: ["account-notes", accountId] });
    },
  });

  const editMut = useMutation({
    mutationFn: ({ noteId, body }: { noteId: number; body: UpdateAccountNoteRequest }) =>
      accountNotesApi.patch(noteId, body),
    onSuccess: () => {
      toast.success("Note updated");
      setEditingId(null);
      void qc.invalidateQueries({ queryKey: ["account-notes", accountId] });
    },
  });

  const notes = (data?.items ?? []).filter((n: AccountNoteDto) => !n.deleted);

  return (
    <div className="space-y-4">
      {isLoading ? (
        <p className="text-sm text-muted-foreground">Loading…</p>
      ) : notes.length === 0 ? (
        <p className="text-sm text-muted-foreground">No notes.</p>
      ) : (
        <ul className="space-y-2">
          {notes.map((note: AccountNoteDto) =>
            editingId === note.noteId ? (
              <li key={note.noteId} className="space-y-1">
                <textarea
                  className={cn("w-full rounded-md border border-input bg-transparent px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-ring")}
                  value={editText}
                  onChange={(e) => setEditText(e.target.value)}
                  rows={3}
                />
                <div className="flex gap-2">
                  <Button size="sm" onClick={() => editMut.mutate({ noteId: note.noteId, body: { text: editText } })} disabled={editMut.isPending}>Save</Button>
                  <Button size="sm" variant="outline" onClick={() => setEditingId(null)}>Cancel</Button>
                </div>
              </li>
            ) : (
              <li key={note.noteId} className="rounded-md border p-3 text-sm">
                <p>{note.text}</p>
                <div className="mt-1 flex items-center gap-2 text-xs text-muted-foreground">
                  <span>{note.createdByName ?? "Unknown"}</span>
                  <span>{note.createdOn ? new Date(note.createdOn).toLocaleString("en-AU") : ""}</span>
                  <button className="underline ml-auto" onClick={() => { setEditingId(note.noteId); setEditText(note.text); }}>Edit</button>
                </div>
              </li>
            )
          )}
        </ul>
      )}

      {/* Add new note */}
      <form
        onSubmit={(e) => { e.preventDefault(); if (newText.trim()) addMut.mutate({ text: newText }); }}
        className="space-y-2"
      >
        <textarea
          className="w-full rounded-md border border-input bg-transparent px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-ring"
          value={newText}
          onChange={(e) => setNewText(e.target.value)}
          placeholder="Add a note…"
          rows={3}
        />
        <Button type="submit" size="sm" disabled={addMut.isPending || !newText.trim()}>Add note</Button>
      </form>
    </div>
  );
}

// ---------------------------------------------------------------------------
// AuditTab
// ---------------------------------------------------------------------------

function AuditTab({ accountId }: { accountId: number }) {
  const { data, isLoading } = useQuery({
    queryKey: ["account-events", accountId],
    queryFn: () => accountsApi.events(accountId),
  });

  if (isLoading) return <p className="text-sm text-muted-foreground">Loading…</p>;
  const events = data?.items ?? [];
  if (events.length === 0) return <p className="text-sm text-muted-foreground">No audit events.</p>;

  return (
    <ul className="space-y-2 text-sm">
      {events.map((ev: AccountEventDto) => (
        <li key={ev.accountEventId} className="flex gap-2">
          <span className="text-muted-foreground whitespace-nowrap">
            {ev.createdOn ? new Date(ev.createdOn).toLocaleString("en-AU") : "—"}
          </span>
          <span>
            <strong>{ev.shortDesc}</strong>
            {ev.description ? ` — ${ev.description}` : ""}
            {ev.createdByName ? <span className="text-muted-foreground"> ({ev.createdByName})</span> : null}
          </span>
        </li>
      ))}
    </ul>
  );
}

// ---------------------------------------------------------------------------
// Right-rail / primary actions
// ---------------------------------------------------------------------------

function AccountActions({ account }: { account: AccountDto }) {
  const qc = useQueryClient();
  const { me } = useAuth();
  const { has } = usePermissions();
  const isAdmin = me?.roles.includes("Admin") ?? false;
  const isFinanceOrAdmin = isAdmin || (me?.roles.includes("Finance") ?? false);
  const canRecordPayment = has("payments:write");
  const canAdjust = has("account-transactions:write");
  const canRequestTermsApproval = has("account-terms:write");
  const canApproveTerms = has("account-terms:accept");
  const [statusDrawerOpen, setStatusDrawerOpen] = useState(false);
  const [editAccountOpen, setEditAccountOpen] = useState(false);
  const [adjustOpen, setAdjustOpen] = useState(false);
  const [requestTermsOpen, setRequestTermsOpen] = useState(false);
  const [approveTermsOpen, setApproveTermsOpen] = useState(false);
  const [confirmRecalcLedger, setConfirmRecalcLedger] = useState(false);
  const [issueDrawerOpen, setIssueDrawerOpen] = useState(false);
  const [emailDrawerOpen, setEmailDrawerOpen] = useState(false);
  const [recordPaymentOpen, setRecordPaymentOpen] = useState(false);
  const [forDate, setForDate] = useState(new Date().toISOString().slice(0, 10));
  const [issueMethod, setIssueMethod] = useState<"Email" | "Print" | "Manual">("Email");
  const [emailTo, setEmailTo] = useState("");

  const recalcAgingMut = useMutation({
    mutationFn: () => accountsApi.recalculateAging(account.accountId),
    onSuccess: () => {
      toast.success("Aging recalculated");
      void qc.invalidateQueries({ queryKey: ["account", account.accountId] });
    },
    onError: () => toast.error("Failed to recalculate"),
  });

  const recalcLedgerMut = useMutation({
    mutationFn: () => accountsApi.recalculateFromLedger(account.accountId),
    onSuccess: () => {
      toast.success("Recalculated from ledger");
      void qc.invalidateQueries({ queryKey: ["account", account.accountId] });
    },
    onError: () => toast.error("Failed to recalculate"),
  });

  const issueMut = useMutation({
    mutationFn: (req: IssueStatementRequest) => accountStatementsApi.issue(account.accountId, req),
    onSuccess: () => {
      toast.success("Statement enqueued");
      setIssueDrawerOpen(false);
      void qc.invalidateQueries({ queryKey: ["account-statements", account.accountId] });
    },
    onError: () => toast.error("Failed to enqueue statement"),
  });

  const emailMut = useMutation({
    mutationFn: (body: EmailStatementRequest) => {
      // Email the most recent statement — resolved via account list
      return accountStatementsApi.list(account.accountId, "pageSize=1").then((r) => {
        const latest = r.items[0];
        if (!latest) throw new Error("No statement found");
        return accountStatementsApi.email(latest.statementId, body);
      });
    },
    onSuccess: () => { toast.success("Email sent"); setEmailDrawerOpen(false); },
    onError: () => toast.error("Send failed"),
  });

  return (
    <TooltipProvider>
      <div className="flex flex-wrap gap-2">
        {/* Edit account — Accounts write access */}
        {has("accounts:write") && (
          <Button variant="outline" onClick={() => setEditAccountOpen(true)}>
            Edit account
          </Button>
        )}

        {/* Change status */}
        <Button variant="outline" onClick={() => setStatusDrawerOpen(true)}>
          Change status
        </Button>

        {/* Recalculate aging — Finance + Admin */}
        {isFinanceOrAdmin && (
          <Button
            variant="outline"
            onClick={() => recalcAgingMut.mutate()}
            disabled={recalcAgingMut.isPending}
          >
            Recalculate aging
          </Button>
        )}

        {/* Recalculate from ledger — Admin only, with confirm */}
        {isAdmin && (
          <>
            {confirmRecalcLedger ? (
              <div className="flex gap-2 items-center">
                <span className="text-xs text-muted-foreground">Are you sure?</span>
                <Button
                  size="sm"
                  variant="destructive"
                  onClick={() => { recalcLedgerMut.mutate(); setConfirmRecalcLedger(false); }}
                  disabled={recalcLedgerMut.isPending}
                >
                  Confirm
                </Button>
                <Button size="sm" variant="outline" onClick={() => setConfirmRecalcLedger(false)}>Cancel</Button>
              </div>
            ) : (
              <Button variant="outline" onClick={() => setConfirmRecalcLedger(true)}>
                Recalculate from ledger
              </Button>
            )}
          </>
        )}

        {/* Plan II live actions */}
        <Link to={`/accounts/${account.accountId}/statements`}>
          <Button variant="outline">Statements</Button>
        </Link>
        <Button variant="outline" onClick={() => setIssueDrawerOpen(true)}>Issue statement</Button>
        <Button variant="outline" onClick={() => { setEmailTo(account.emailAddress ?? ""); setEmailDrawerOpen(true); }} disabled={!account.lastStatementIssuedDate}>Email statement</Button>
        <Link to="/admin/account-terms">
          <Button variant="outline">T&amp;C templates</Button>
        </Link>

        {/* Record payment — Payments write access */}
        {canRecordPayment && (
          <Button onClick={() => setRecordPaymentOpen(true)}>Record payment</Button>
        )}

        {/* Adjust — manual credit/debit/write-off, account-transactions write access */}
        {canAdjust && (
          <Button variant="outline" onClick={() => setAdjustOpen(true)}>Adjust</Button>
        )}

        {/* Request terms approval — emails the customer an external accept link */}
        {canRequestTermsApproval && (
          <Button variant="outline" onClick={() => setRequestTermsOpen(true)}>Request terms approval</Button>
        )}

        {/* Approve terms — records acceptance internally on the customer's behalf */}
        {canApproveTerms && (
          <Button variant="outline" onClick={() => setApproveTermsOpen(true)}>Approve terms</Button>
        )}

        <RecordPaymentModal
          open={recordPaymentOpen}
          onOpenChange={setRecordPaymentOpen}
          accountId={account.accountId}
          onSuccess={() => {
            void qc.invalidateQueries({ queryKey: ["account", account.accountId] });
            void qc.invalidateQueries({ queryKey: ["account-tx", account.accountId] });
          }}
        />

        <ChangeStatusDrawer
          open={statusDrawerOpen}
          onOpenChange={setStatusDrawerOpen}
          currentStatus={account.accountStatusCode}
          accountId={account.accountId}
        />

        <EditAccountDialog
          open={editAccountOpen}
          onOpenChange={setEditAccountOpen}
          account={account}
        />

        <AdjustAccountDialog
          open={adjustOpen}
          onOpenChange={setAdjustOpen}
          accountId={account.accountId}
        />

        <RequestTermsApprovalDialog
          open={requestTermsOpen}
          onOpenChange={setRequestTermsOpen}
          account={account}
        />

        <ApproveTermsDialog
          open={approveTermsOpen}
          onOpenChange={setApproveTermsOpen}
          accountId={account.accountId}
        />

        {/* Issue statement drawer */}
        <DetailModal open={issueDrawerOpen} onOpenChange={setIssueDrawerOpen} title="Issue statement">
          <form
            className="space-y-3"
            onSubmit={(e) => {
              e.preventDefault();
              issueMut.mutate({ forDate: new Date(forDate).toISOString(), outstandingOverride: null, issueMethod });
            }}
          >
            <div>
              <Label htmlFor="rail-for-date">For date</Label>
              <Input id="rail-for-date" type="date" value={forDate} onChange={(e) => setForDate(e.target.value)} />
            </div>
            <div>
              <Label htmlFor="rail-method">Issue method</Label>
              <Select value={issueMethod} onValueChange={(v) => setIssueMethod(v as typeof issueMethod)}>
                <SelectTrigger id="rail-method"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="Email">Email</SelectItem>
                  <SelectItem value="Print">Print</SelectItem>
                  <SelectItem value="Manual">Manual</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <Button type="submit" disabled={issueMut.isPending}>
              {issueMut.isPending ? "Enqueuing…" : "Generate"}
            </Button>
          </form>
        </DetailModal>

        {/* Email statement drawer */}
        <DetailModal open={emailDrawerOpen} onOpenChange={setEmailDrawerOpen} title="Email latest statement">
          <form className="space-y-3" onSubmit={(e) => { e.preventDefault(); emailMut.mutate({ toEmailAddress: emailTo, ccEmailAddress: null }); }}>
            <div>
              <Label htmlFor="rail-email-to">To</Label>
              <Input id="rail-email-to" type="email" value={emailTo} onChange={(e) => setEmailTo(e.target.value)} required />
            </div>
            <Button type="submit" disabled={emailMut.isPending}>{emailMut.isPending ? "Sending…" : "Send"}</Button>
          </form>
        </DetailModal>
      </div>
    </TooltipProvider>
  );
}

// ---------------------------------------------------------------------------
// AccountDetailPage
// ---------------------------------------------------------------------------

export function AccountDetailPage() {
  const { id = "0" } = useParams();
  const accountId = Number(id);

  const { data: account } = useQuery({
    queryKey: ["account", accountId],
    queryFn: () => accountsApi.get(accountId),
  });

  const tabs = account
    ? [
        {
          id: "overview",
          label: "Overview",
          content: <OverviewTab account={account} />,
        },
        {
          id: "transactions",
          label: "Transactions",
          content: <TransactionsTab accountId={accountId} openingBalance={account.openingBalance} />,
        },
        {
          id: "notes",
          label: "Notes",
          content: <NotesTab accountId={accountId} />,
        },
        {
          id: "statements",
          label: "Statements",
          content: <StatementsTab accountId={accountId} />,
        },
        {
          id: "terms",
          label: "Terms",
          content: <TermsTab accountId={accountId} />,
        },
        {
          id: "emails",
          label: "Emails",
          content: <EmailThreadTab entityType="Account" entityId={accountId} />,
        },
        {
          id: "audit",
          label: "Audit",
          content: <AuditTab accountId={accountId} />,
        },
      ]
    : [];

  return (
    <DetailPage
      title={account ? `${account.accountReferenceNo} — ${account.companyName ?? account.clientFullName ?? "Account"}` : "Loading…"}
      tabActions={account ? <AccountActions account={account} /> : undefined}
      tabs={tabs}
      defaultTab="overview"
    />
  );
}
